import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { API_URL } from '../config';
import { toast } from 'react-toastify';

function AdminPayouts() {
    const [payouts, setPayouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [modalAction, setModalAction] = useState(''); // 'approve' or 'reject'
    const [notes, setNotes] = useState('');
    const [processing, setProcessing] = useState(false);
    
    const navigate = useNavigate();
    const location = useLocation();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    useEffect(() => {
        if (!token || role !== 'ADMIN') {
            navigate('/admin-login');
            return;
        }
        fetchPayouts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filter]);

    const fetchPayouts = async () => {
        setLoading(true);
        try {
            const url = filter
                ? `${API_URL}/api/admin/payouts?status=${filter}&page=${page}&size=20`
                : `${API_URL}/api/admin/payouts?page=${page}&size=20`;
            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = res.data.content || res.data;
            setPayouts(data);
            setTotalPages(res.data.totalPages || 1);
            setTotalElements(res.data.totalElements || data.length);
        } catch (err) {
            toast.error('Failed to load payouts');
        }
        setLoading(false);
    };

    const openModal = (payout, action) => {
        setSelectedPayout(payout);
        setModalAction(action);
        setNotes('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedPayout(null);
        setModalAction('');
        setNotes('');
    };

    const handleConfirmAction = async () => {
        if (!selectedPayout) return;

        setProcessing(true);
        try {
            await axios.put(
                `${API_URL}/api/admin/payouts/${selectedPayout.id}/${modalAction}`,
                { notes: notes || '' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Payout ${modalAction}d successfully!`);
            closeModal();
            fetchPayouts();
        } catch (err) {
            toast.error(err.response?.data?.message || `Failed to ${modalAction} payout`);
        }
        setProcessing(false);
    };

    const handleLogout = () => {
        localStorage.clear();
        navigate('/');
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING': return 'badge badge-warning';
            case 'APPROVED': case 'COMPLETED': return 'badge badge-success';
            case 'REJECTED': return 'badge badge-error';
            default: return 'badge';
        }
    };

    return (
        <div className="dashboard-layout">
            {/* SIDEBAR */}
            <aside className="sidebar" style={{ borderRight: '1px solid var(--error)' }}>
                <Link to="/" className="sidebar-brand">infloir <span style={{ color: 'var(--error)', fontSize: '12px' }}>ADMIN</span></Link>
                <nav className="sidebar-nav">
                    <Link to="/admin-dashboard" className="nav-item">
                        <span>🛡️</span> Overview
                    </Link>
                    <Link to="/admin/payouts" className={`nav-item ${location.pathname === '/admin/payouts' ? 'active' : ''}`}>
                        <span>💸</span> Manage Payouts
                    </Link>
                    <Link to="/admin/revenue" className="nav-item">
                        <span>💰</span> Revenue
                    </Link>
                </nav>
                <div style={{ marginTop: 'auto' }}>
                    <button onClick={handleLogout} className="nav-item" style={{ width: '100%', background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer' }}>
                        <span>🚪</span> Log out
                    </button>
                </div>
            </aside>

            {/* MAIN CONTENT */}
            <div className="main-content">
                <header className="topbar">
                    <h2 style={{ fontSize: '18px', fontWeight: '500' }}>Payout Requests</h2>
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{userEmail}</span>
                </header>

                <main className="page-container page-fade-in">
                    
                    {/* FILTERS */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
                        {['', 'PENDING', 'APPROVED', 'COMPLETED', 'REJECTED'].map(status => (
                            <button
                                key={status || 'ALL'}
                                onClick={() => { setFilter(status); setPage(0); }}
                                className={filter === status ? 'btn-primary' : 'btn-secondary'}
                                style={{ padding: '6px 16px', fontSize: '13px' }}
                            >
                                {status || 'All'}
                            </button>
                        ))}
                    </div>

                    {/* PAYOUTS TABLE */}
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                            <div className="spinner" style={{ width: '24px', height: '24px', borderTopColor: '#fff' }}></div>
                        </div>
                    ) : payouts.length === 0 ? (
                        <div className="card" style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-muted)' }}>
                            <p style={{ fontSize: '32px', marginBottom: '12px' }}>💸</p>
                            <p>No payout requests found for this filter.</p>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: '16px' }}>
                                <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                    Showing <strong style={{ color: 'var(--text)' }}>{totalElements}</strong> total requests
                                </p>
                            </div>

                            <div className="table-container">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Worker</th>
                                            <th>Amount</th>
                                            <th>Requested</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payouts.map(p => (
                                            <tr key={p.id}>
                                                <td style={{ fontWeight: '500', color: 'var(--text)' }}>{p.workerEmail}</td>
                                                <td style={{ fontWeight: '600', color: 'var(--success)' }}>${parseFloat(p.amount).toFixed(2)}</td>
                                                <td style={{ fontSize: '13px' }}>{new Date(p.requestedAt).toLocaleString()}</td>
                                                <td><span className={getStatusBadge(p.status)}>{p.status}</span></td>
                                                <td>
                                                    {p.status === 'PENDING' ? (
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button onClick={() => openModal(p, 'approve')} className="btn-primary" style={{ padding: '6px 12px', fontSize: '12px' }}>Approve</button>
                                                            <button onClick={() => openModal(p, 'reject')} className="btn-danger" style={{ padding: '6px 12px', fontSize: '12px' }}>Reject</button>
                                                        </div>
                                                    ) : (
                                                        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Reviewed</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* PAGINATION */}
                            {totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '24px' }}>
                                    <button
                                        onClick={() => setPage(p => Math.max(0, p - 1))}
                                        disabled={page === 0}
                                        className="btn-secondary"
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        ← Previous
                                    </button>
                                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                                        Page {page + 1} of {totalPages}
                                    </span>
                                    <button
                                        onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                                        disabled={page >= totalPages - 1}
                                        className="btn-secondary"
                                        style={{ padding: '8px 16px', fontSize: '13px' }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>

            {/* CONFIRMATION MODAL */}
            {showModal && selectedPayout && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(8px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px' }} onClick={closeModal}>
                    <div className="card" style={{ width: '100%', maxWidth: '450px', padding: '32px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 style={{ fontSize: '20px', marginBottom: '8px', color: modalAction === 'approve' ? 'var(--success)' : 'var(--error)' }}>
                            {modalAction === 'approve' ? '✓ Approve Payout' : '✕ Reject Payout'}
                        </h3>
                        <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '24px', lineHeight: '1.6' }}>
                            Worker: <strong style={{ color: 'var(--text)' }}>{selectedPayout.workerEmail}</strong><br />
                            Amount: <strong style={{ color: 'var(--text)' }}>${parseFloat(selectedPayout.amount).toFixed(2)} USD</strong>
                        </p>

                        <div className="form-field">
                            <label className="form-label">Notes (Optional)</label>
                            <textarea
                                placeholder="Add a note for the worker..."
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                style={{ minHeight: '80px' }}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                            <button onClick={closeModal} className="btn-secondary" disabled={processing}>
                                Cancel
                            </button>
                            <button 
                                onClick={handleConfirmAction} 
                                className={modalAction === 'approve' ? 'btn-primary' : 'btn-danger'} 
                                disabled={processing}
                            >
                                {processing ? 'Processing...' : `Confirm ${modalAction === 'approve' ? 'Approval' : 'Rejection'}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPayouts;